import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Upload, Plus, X, Loader2, Trash2, LogOut, ListMusic, FileText, Settings, Music, Sparkles, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { RELEASE_TYPES } from '@/constants/releaseTypes';
import { readAudioMetadata, blobToFile } from '@/lib/audioMetadata';
import WaveformTrimmer from '@/components/WaveformTrimmer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Collapsible waveform trimmer for a single track. Renders a small summary row
 * (start-time display + "Edit preview section" button) by default so 10 tracks can
 * coexist without decoding 10 waveforms into memory at once. Expands on demand.
 */
const TrackPreviewTrimmer = ({ index, track, setTracks }) => {
  const [expanded, setExpanded] = useState(false);
  const start = Number(track.preview_start) || 0;
  const mmSs = (s) => `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
  return (
    <div className="pt-2 border-t border-[#27272A]" data-testid={`track-trimmer-wrap-${index}`}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <label className="text-xs text-[#A1A1AA] flex items-center gap-2">
          <Music size={12} className="text-[#00F0FF]" />
          30-sec preview starts at
          <span className="font-mono text-[#00F0FF]" data-testid={`track-trimmer-${index}-summary`}>
            {mmSs(start)}
          </span>
        </label>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-[11px] px-3 py-1 rounded-full bg-[#18181B] border border-[#27272A] hover:border-[#00F0FF] hover:text-[#00F0FF] transition"
          data-testid={`track-trimmer-${index}-toggle`}
        >
          {expanded ? 'Hide waveform' : 'Edit preview section'}
        </button>
      </div>
      {expanded && (
        <WaveformTrimmer
          file={track.full_file}
          defaultStart={start}
          regionSeconds={30}
          testIdPrefix={`track-trimmer-${index}`}
          onChange={({ start: newStart }) => {
            setTracks((prev) => {
              const next = [...prev];
              next[index] = { ...next[index], preview_start: newStart };
              return next;
            });
          }}
        />
      )}
    </div>
  );
};

const AdminUploadPage = () => {
  const { admin, logout } = useAuth();
  const [genres, setGenres] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadStep, setUploadStep] = useState('');
  const [showAddGenre, setShowAddGenre] = useState(false);
  const [newGenreName, setNewGenreName] = useState('');
  const [addingGenre, setAddingGenre] = useState(false);
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    title: '',
    artist: '',
    release_type: 'exclusive',
    genre_ids: [],
    price: '',
    description: '',
  });
  
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState('');
  const dropRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [tracks, setTracks] = useState([
    { title: '', duration: '3:30', preview_file: null, full_file: null, bpm: '', key: '', version: 'Original Mix', file_type: 'MP3 320', preview_start: 0 }
  ]);

  useEffect(() => {
    fetchGenres();
  }, []);

  const fetchGenres = async () => {
    try {
      const response = await axios.get(`${API}/genres`);
      if (response.data.length === 0) {
        await createDefaultGenres();
      } else {
        setGenres(response.data);
      }
    } catch (error) {
      console.error('Error fetching genres:', error);
    }
  };

  const createDefaultGenres = async () => {
    const defaultGenres = ['House', 'Techno', 'Trance', 'Dubstep', 'Drum & Bass'];
    try {
      for (const name of defaultGenres) {
        await axios.post(`${API}/genres`, { name });
      }
      await fetchGenres();
    } catch (error) {
      console.error('Error creating genres:', error);
    }
  };

  const handleAddGenre = async () => {
    const name = newGenreName.trim();
    if (!name) {
      toast.error('Please enter a genre name');
      return;
    }
    setAddingGenre(true);
    try {
      const response = await axios.post(`${API}/genres`, { name });
      const newGenre = response.data;
      setGenres([...genres, newGenre]);
      setFormData({ ...formData, genre_ids: [...formData.genre_ids, newGenre.id] });
      setNewGenreName('');
      setShowAddGenre(false);
      toast.success(`Genre "${name}" added!`);
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error('That genre already exists');
      } else {
        toast.error('Failed to add genre');
      }
    } finally {
      setAddingGenre(false);
    }
  };

  const handleDeleteGenre = async (genreId, genreName) => {
    if (!window.confirm(`Delete genre "${genreName}"?`)) return;
    try {
      await axios.delete(`${API}/genres/${genreId}`);
      const remaining = genres.filter(g => g.id !== genreId);
      setGenres(remaining);
      setFormData({ ...formData, genre_ids: formData.genre_ids.filter(id => id !== genreId) });
      toast.success('Genre deleted');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Cannot delete: genre is used by one or more releases');
      } else {
        toast.error('Failed to delete genre');
      }
    }
  };

  const toggleGenre = (genreId) => {
    setFormData(prev => ({
      ...prev,
      genre_ids: prev.genre_ids.includes(genreId)
        ? prev.genre_ids.filter(id => id !== genreId)
        : [...prev.genre_ids, genreId],
    }));
  };

  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCoverFile(file);
      setCoverPreview(URL.createObjectURL(file));
    }
  };

  const handleTrackFileChange = (index, type, file) => {
    const newTracks = [...tracks];
    newTracks[index][type === 'preview' ? 'preview_file' : 'full_file'] = file;
    setTracks(newTracks);
  };

  const addTrack = () => {
    setTracks([...tracks, { title: '', duration: '3:30', preview_file: null, full_file: null, bpm: '', key: '', version: 'Original Mix', file_type: 'MP3 320', preview_start: 0 }]);
  };

  /**
   * Ingest one or more dropped audio files: reads ID3 tags, extracts embedded cover art,
   * auto-fills every editable field, and appends new tracks. First cover art found is used
   * as the album cover if none is set yet.
   */
  const ingestAudioFiles = async (files) => {
    const audioFiles = Array.from(files).filter((f) => /^audio\//.test(f.type) || /\.(mp3|wav|aiff?|flac|m4a)$/i.test(f.name));
    if (audioFiles.length === 0) {
      toast.error('Drop MP3/WAV/AIFF/FLAC audio files only.');
      return;
    }
    setScanning(true);
    try {
      const newTracks = [];
      let firstCover = null;
      const genreNamesToMatch = [];
      for (const f of audioFiles) {
        const meta = await readAudioMetadata(f);
        newTracks.push({
          title: meta.title || '',
          duration: meta.duration || '3:30',
          preview_file: null, // will be generated from the trimmer selection
          full_file: f,
          bpm: meta.bpm || '',
          key: meta.key || '',
          version: 'Original Mix',
          file_type: meta.fileType || 'MP3 320',
          preview_start: 0,
        });
        if (!firstCover && meta.coverBlob) {
          firstCover = blobToFile(
            meta.coverBlob,
            `cover-${Date.now()}.${(meta.coverMime || 'image/jpeg').split('/')[1]}`,
          );
        }
        if (meta.genre) genreNamesToMatch.push(meta.genre);
      }

      // Merge: replace the first empty track (if any), append the rest
      setTracks((prev) => {
        const firstIsBlank = prev.length === 1 && !prev[0].full_file && !prev[0].title;
        return firstIsBlank ? newTracks : [...prev, ...newTracks];
      });

      // Auto-set album cover from first embedded artwork (only if none picked yet)
      if (firstCover && !coverFile) {
        setCoverFile(firstCover);
        setCoverPreview(URL.createObjectURL(firstCover));
      }

      // Auto-select matching genres (case-insensitive) that already exist in the store
      if (genreNamesToMatch.length) {
        setFormData((prev) => {
          const lc = genreNamesToMatch.map((g) => g.toLowerCase());
          const matched = genres.filter((g) => lc.includes((g.name || '').toLowerCase())).map((g) => g.id);
          const next = Array.from(new Set([...(prev.genre_ids || []), ...matched]));
          return { ...prev, genre_ids: next };
        });
      }

      toast.success(`Read metadata from ${audioFiles.length} file${audioFiles.length > 1 ? 's' : ''}`);
    } catch (err) {
      console.error('Metadata scan error:', err);
      toast.error('Could not read some file metadata. You can still fill it in manually.');
    } finally {
      setScanning(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = e.dataTransfer?.files;
    if (files?.length) ingestAudioFiles(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };
  const handleDropPickerChange = (e) => {
    const files = e.target.files;
    if (files?.length) ingestAudioFiles(files);
    e.target.value = ''; // allow re-selecting the same file
  };

  const removeTrack = (index) => {
    setTracks(tracks.filter((_, i) => i !== index));
  };

  const uploadFile = async (file, endpoint) => {
    const formData = new FormData();
    formData.append('file', file);
    if (endpoint.includes('audio')) {
      formData.append('file_type', endpoint.includes('preview') ? 'preview' : 'full');
    }
    // 8-minute per-file timeout: enough for a 100MB HQ upload on a slow connection,
    // but bounded so a stuck request eventually surfaces an error to the UI.
    const response = await axios.post(`${API}${endpoint}`, formData, { timeout: 8 * 60 * 1000 });
    return response.data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.genre_ids.length === 0) {
      toast.error('Please select at least one genre');
      return;
    }
    if (!coverFile) {
      toast.error('Please upload a cover image (or drop an MP3 with embedded art).');
      return;
    }
    // Every track must have a full audio file (previews are auto-generated if missing)
    const missingFull = tracks.findIndex((t) => !t.full_file);
    if (missingFull >= 0) {
      toast.error(`Track ${missingFull + 1} needs a full audio file — drop one at the top or use Replace.`);
      return;
    }
    // Every track must have a title
    const missingTitle = tracks.findIndex((t) => !t.title || !t.title.trim());
    if (missingTitle >= 0) {
      toast.error(`Track ${missingTitle + 1} is missing a title.`);
      return;
    }
    setLoading(true);
    setUploadStep('Uploading cover…');

    try {
      // Upload cover
      const coverResp = await uploadFile(coverFile, '/upload/cover');
      const coverUrl = coverResp.url;

      // ------- Parallel per-track pipeline -------
      // Each track: uploadFullHQ → (uploadPreviewOverride OR generatePreview30s).
      // We run up to CONCURRENCY tracks at a time. Ordering of the finished array
      // is preserved so the album's tracks[] on the backend matches the admin's UI.
      const CONCURRENCY = 3;
      const uploadedTracks = new Array(tracks.length);
      let completed = 0;

      const processTrack = async (i) => {
        const track = tracks[i];
        // Upload HQ full audio
        const fullResp = await uploadFile(track.full_file, '/upload/audio');
        // Build preview
        let previewUrlAbs;
        if (track.preview_file) {
          const previewResp = await uploadFile(track.preview_file, '/upload/audio');
          previewUrlAbs = `${BACKEND_URL}${previewResp.url}`;
        } else {
          const cutResp = await axios.post(`${API}/admin/preview/from-full`, {
            full_file_path: fullResp.path,
            start_seconds: Number(track.preview_start) || 0,
            duration_seconds: 30,
          });
          previewUrlAbs = `${BACKEND_URL}${cutResp.data.url}`;
        }
        uploadedTracks[i] = {
          title: track.title,
          duration: track.duration,
          preview_url: previewUrlAbs,
          full_file_path: fullResp.path,
          bpm: track.bpm ? parseInt(track.bpm) : null,
          key: track.key || null,
          version: track.version || null,
          file_type: track.file_type || null,
        };
        completed += 1;
        setUploadStep(`Uploaded ${completed} of ${tracks.length} tracks…`);
      };

      // Simple worker pool: pull the next track index off a queue as each worker frees up.
      const queue = tracks.map((_, i) => i);
      const workers = Array.from({ length: Math.min(CONCURRENCY, tracks.length) }).map(async () => {
        while (queue.length) {
          const idx = queue.shift();
          if (idx === undefined) return;
          await processTrack(idx);
        }
      });
      setUploadStep(`Uploading tracks 1-${Math.min(CONCURRENCY, tracks.length)} of ${tracks.length}…`);
      await Promise.all(workers);

      // Create album
      setUploadStep('Saving release…');
      const albumData = {
        ...formData,
        cover_url: coverUrl,
        price: parseFloat(formData.price),
        tracks: uploadedTracks,
      };

      await axios.post(`${API}/albums`, albumData);

      setUploadStep('Done ✓');
      toast.success('Album uploaded successfully!');
      
      // Reset form
      setFormData({
        title: '',
        artist: '',
        release_type: 'exclusive',
        genre_ids: [],
        price: '',
        description: '',
      });
      setCoverFile(null);
      setCoverPreview('');
      setTracks([{ title: '', duration: '3:30', preview_file: null, full_file: null, bpm: '', key: '', version: 'Original Mix', file_type: 'MP3 320', preview_start: 0 }]);
      
      // Navigate to releases page after 1s so admin can see + edit uploads
      setTimeout(() => navigate('/admin/releases'), 1000);
    } catch (error) {
      console.error('Upload error:', error);
      const detail = error?.response?.data?.detail;
      toast.error(detail ? `Upload failed: ${detail}` : 'Failed to upload album. Please try again.');
      setUploadStep('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold mb-2">Upload New Release</h1>
            <p className="text-[#A1A1AA]">Add a new album or single to your store</p>
          </div>
          <div className="flex items-center gap-3">
            {admin && admin.email && (
              <span className="text-sm text-[#A1A1AA]" data-testid="admin-email-display">
                {admin.email}
              </span>
            )}
            <Button
              variant="outline"
              onClick={() => navigate('/admin/orders/review')}
              className="border-[#27272A] hover:bg-[#18181B] rounded-full"
              data-testid="review-orders-button"
            >
              <ShieldAlert size={16} className="mr-2" /> Review orders
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/releases')}
              className="border-[#27272A] hover:bg-[#18181B] rounded-full"
              data-testid="my-releases-button"
            >
              <ListMusic size={16} className="mr-2" /> My Releases
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/pages')}
              className="border-[#27272A] hover:bg-[#18181B] rounded-full"
              data-testid="site-pages-button"
            >
              <FileText size={16} className="mr-2" /> Site Pages
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/settings')}
              className="border-[#27272A] hover:bg-[#18181B] rounded-full"
              data-testid="site-settings-button"
            >
              <Settings size={16} className="mr-2" /> Settings
            </Button>
            <Button
              variant="outline"
              onClick={async () => { await logout(); navigate('/'); }}
              className="border-[#27272A] hover:bg-[#18181B] rounded-full"
              data-testid="admin-logout-button"
            >
              <LogOut size={16} className="mr-2" /> Log out
            </Button>
          </div>
        </div>

        {/* Manage Genres Card */}
        <div className="bg-[#121214] border border-[#27272A] rounded-lg p-6 mb-6" data-testid="manage-genres-card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-heading font-semibold">Your Genres</h2>
            <span className="text-xs text-[#A1A1AA]">{genres.length} total</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {genres.map((g) => (
              <div key={g.id} className="group flex items-center gap-1 pl-3 pr-1 py-1 bg-[#18181B] border border-[#27272A] rounded-full text-sm" data-testid={`genre-chip-${g.slug}`}>
                <span>{g.name}</span>
                <button
                  type="button"
                  onClick={() => handleDeleteGenre(g.id, g.name)}
                  className="w-5 h-5 rounded-full text-[#A1A1AA] hover:text-[#EF4444] hover:bg-[#27272A] flex items-center justify-center transition-colors"
                  title={`Delete ${g.name}`}
                  data-testid={`delete-genre-${g.slug}`}
                >
                  <X size={12} />
                </button>
              </div>
            ))}
            {genres.length === 0 && (
              <p className="text-sm text-[#A1A1AA]">No genres yet. Add one below when you create a release.</p>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-[#121214] border border-[#27272A] rounded-lg p-8 space-y-6">
          {/* Drop zone — reads ID3 metadata & auto-fills every track field */}
          <div
            ref={dropRef}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`relative rounded-lg border-2 border-dashed p-6 transition-colors ${
              dragActive
                ? 'border-[#00F0FF] bg-[#00F0FF]/5'
                : 'border-[#27272A] hover:border-[#3F3F46] bg-[#0F0F11]'
            }`}
            data-testid="admin-audio-dropzone"
          >
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-[#00F0FF]/10 flex items-center justify-center">
                  {scanning ? (
                    <Loader2 size={20} className="text-[#00F0FF] animate-spin" />
                  ) : (
                    <Sparkles size={20} className="text-[#00F0FF]" />
                  )}
                </div>
                <div>
                  <p className="font-medium text-sm">Drop MP3 / WAV / FLAC here — this becomes the download file</p>
                  <p className="text-xs text-[#71717A]">Auto-fills title, BPM, key, genre, duration, cover art & file type from ID3 tags. Each dropped file becomes a track.</p>
                </div>
              </div>
              <label className="inline-flex">
                <input
                  type="file"
                  accept="audio/*"
                  multiple
                  onChange={handleDropPickerChange}
                  className="hidden"
                  data-testid="admin-audio-picker"
                />
                <span className="text-xs px-4 py-2 rounded-full bg-[#00F0FF] hover:bg-[#33F3FF] text-black font-medium cursor-pointer transition">
                  Or browse files
                </span>
              </label>
            </div>
          </div>

          {/* Basic Info */}
          <div className="space-y-4">
            <h2 className="text-xl font-heading font-semibold">Basic Information</h2>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="bg-[#18181B] border-[#27272A]"
                  data-testid="input-title"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Artist *</label>
                <Input
                  value={formData.artist}
                  onChange={(e) => setFormData({ ...formData, artist: e.target.value })}
                  required
                  className="bg-[#18181B] border-[#27272A]"
                  data-testid="input-artist"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Release Type *</label>
                <select
                  value={formData.release_type}
                  onChange={(e) => setFormData({ ...formData, release_type: e.target.value })}
                  className="w-full h-10 px-3 bg-[#18181B] border border-[#27272A] rounded-md text-white"
                  data-testid="select-release-type"
                >
                  {RELEASE_TYPES.map(rt => (
                    <option key={rt.value} value={rt.value}>{rt.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Price (₱) *</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                  className="bg-[#18181B] border-[#27272A]"
                  data-testid="input-price"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium">Genres * (select one or more)</label>
                <button
                  type="button"
                  onClick={() => setShowAddGenre(!showAddGenre)}
                  className="text-xs text-[#00F0FF] hover:text-[#33F3FF] flex items-center gap-1"
                  data-testid="toggle-add-genre-button"
                >
                  <Plus size={12} /> New
                </button>
              </div>
              <div className="flex flex-wrap gap-2 p-3 bg-[#18181B] border border-[#27272A] rounded-md min-h-[52px]" data-testid="genre-multiselect">
                {genres.length === 0 && (
                  <span className="text-xs text-[#A1A1AA]">No genres yet — add one below.</span>
                )}
                {genres.map(g => {
                  const active = formData.genre_ids.includes(g.id);
                  return (
                    <button
                      type="button"
                      key={g.id}
                      onClick={() => toggleGenre(g.id)}
                      className={`px-3 py-1 rounded-full text-sm transition-colors ${active ? 'bg-[#00F0FF] text-black' : 'bg-[#09090B] text-[#A1A1AA] border border-[#27272A] hover:text-white'}`}
                      data-testid={`toggle-genre-${g.slug}`}
                    >
                      {g.name}
                    </button>
                  );
                })}
              </div>
              {showAddGenre && (
                <div className="mt-2 flex gap-2" data-testid="add-genre-inline">
                  <Input
                    value={newGenreName}
                    onChange={(e) => setNewGenreName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddGenre())}
                    placeholder="e.g. Deep House"
                    className="bg-[#09090B] border-[#27272A]"
                    data-testid="input-new-genre-name"
                  />
                  <Button
                    type="button"
                    onClick={handleAddGenre}
                    disabled={addingGenre}
                    className="bg-[#00F0FF] hover:bg-[#33F3FF] text-black rounded-md"
                    data-testid="submit-new-genre-button"
                  >
                    {addingGenre ? <Loader2 size={16} className="animate-spin" /> : 'Add'}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Cover Image */}
          <div>
            <h2 className="text-xl font-heading font-semibold mb-4">Cover Image</h2>
            <div className="flex gap-4">
              {coverPreview && (
                <img src={coverPreview} alt="Cover preview" className="w-32 h-32 rounded-lg object-cover" />
              )}
              <div className="flex-1">
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-[#27272A] rounded-lg cursor-pointer hover:border-[#00F0FF] transition-colors">
                  <Upload className="mb-2 text-[#A1A1AA]" size={32} />
                  <span className="text-sm text-[#A1A1AA]">Click to upload cover image</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCoverChange}
                    className="hidden"
                    data-testid="input-cover"
                  />
                </label>
              </div>
            </div>
          </div>

          {/* Tracks */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-heading font-semibold">Tracks</h2>
              <Button type="button" onClick={addTrack} variant="outline" size="sm" className="border-[#27272A]" data-testid="add-track-button">
                <Plus size={16} className="mr-2" /> Add Track
              </Button>
            </div>
            
            <div className="space-y-4">
              {tracks.map((track, index) => (
                <div key={index} className="bg-[#18181B] border border-[#27272A] rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium">Track {index + 1}</h3>
                    {tracks.length > 1 && (
                      <button type="button" onClick={() => removeTrack(index)} className="text-[#A1A1AA] hover:text-[#EF4444]">
                        <X size={20} />
                      </button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-[#A1A1AA] mb-1">Track Title *</label>
                      <Input
                        placeholder="e.g., Night Drive"
                        value={track.title}
                        onChange={(e) => {
                          const newTracks = [...tracks];
                          newTracks[index].title = e.target.value;
                          setTracks(newTracks);
                        }}
                        required
                        className="bg-[#09090B] border-[#27272A]"
                        data-testid={`input-track-title-${index}`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-[#A1A1AA] mb-1">Duration *</label>
                      <Input
                        placeholder="e.g., 3:30"
                        value={track.duration}
                        onChange={(e) => {
                          const newTracks = [...tracks];
                          newTracks[index].duration = e.target.value;
                          setTracks(newTracks);
                        }}
                        required
                        className="bg-[#09090B] border-[#27272A]"
                        data-testid={`input-track-duration-${index}`}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs text-[#A1A1AA] mb-1">BPM</label>
                      <Input
                        placeholder="128"
                        value={track.bpm}
                        onChange={(e) => {
                          const newTracks = [...tracks];
                          newTracks[index].bpm = e.target.value;
                          setTracks(newTracks);
                        }}
                        className="bg-[#09090B] border-[#27272A]"
                        data-testid={`input-track-bpm-${index}`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-[#A1A1AA] mb-1">Key</label>
                      <Input
                        placeholder="Am"
                        value={track.key}
                        onChange={(e) => {
                          const newTracks = [...tracks];
                          newTracks[index].key = e.target.value;
                          setTracks(newTracks);
                        }}
                        className="bg-[#09090B] border-[#27272A]"
                        data-testid={`input-track-key-${index}`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-[#A1A1AA] mb-1">Version</label>
                      <select
                        value={track.version || 'Original Mix'}
                        onChange={(e) => {
                          const newTracks = [...tracks];
                          newTracks[index].version = e.target.value;
                          setTracks(newTracks);
                        }}
                        className="w-full h-10 px-2 bg-[#09090B] border border-[#27272A] rounded-md text-white text-sm"
                        data-testid={`select-track-version-${index}`}
                      >
                        <option>Original Mix</option>
                        <option>Extended Mix</option>
                        <option>Radio Edit</option>
                        <option>Club Mix</option>
                        <option>VIP Mix</option>
                        <option>Instrumental</option>
                        <option>Acapella</option>
                        <option>Remix</option>
                        <option>Bootleg</option>
                        <option>Mashup</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-[#A1A1AA] mb-1">File Type</label>
                      <select
                        value={track.file_type || 'MP3 320'}
                        onChange={(e) => {
                          const newTracks = [...tracks];
                          newTracks[index].file_type = e.target.value;
                          setTracks(newTracks);
                        }}
                        className="w-full h-10 px-2 bg-[#09090B] border border-[#27272A] rounded-md text-white text-sm"
                        data-testid={`select-track-filetype-${index}`}
                      >
                        <option>MP3 320</option>
                        <option>MP3 256</option>
                        <option>WAV</option>
                        <option>WAV 24bit</option>
                        <option>FLAC</option>
                        <option>AIFF</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-[#A1A1AA] mb-1">Full Audio (HQ) — served untouched to buyers *</label>
                      {track.full_file ? (
                        <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-[#0F0F11] border border-[#27272A]" data-testid={`track-full-loaded-${index}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            <Music size={14} className="text-[#00F0FF] shrink-0" />
                            <span className="text-xs text-white truncate">{track.full_file.name}</span>
                            <span className="text-[10px] text-[#71717A] shrink-0">
                              ({(track.full_file.size / (1024 * 1024)).toFixed(1)} MB)
                            </span>
                          </div>
                          <label className="inline-flex shrink-0">
                            <input
                              type="file"
                              accept="audio/*"
                              onChange={async (e) => {
                                const f = e.target.files[0];
                                if (!f) return;
                                // Re-run ID3 auto-fill for THIS track only
                                setScanning(true);
                                try {
                                  const meta = await readAudioMetadata(f);
                                  setTracks((prev) => {
                                    const next = [...prev];
                                    next[index] = {
                                      ...next[index],
                                      full_file: f,
                                      title: next[index].title || meta.title || '',
                                      duration: meta.duration || next[index].duration,
                                      bpm: next[index].bpm || meta.bpm || '',
                                      key: next[index].key || meta.key || '',
                                      file_type: meta.fileType || next[index].file_type,
                                      preview_start: 0,
                                    };
                                    return next;
                                  });
                                  toast.success(`Replaced audio for track ${index + 1}`);
                                } finally {
                                  setScanning(false);
                                }
                                e.target.value = '';
                              }}
                              className="hidden"
                              data-testid={`replace-track-full-${index}`}
                            />
                            <span className="text-[11px] px-3 py-1 rounded-full bg-[#18181B] border border-[#27272A] hover:border-[#00F0FF] hover:text-[#00F0FF] cursor-pointer transition">
                              Replace
                            </span>
                          </label>
                        </div>
                      ) : (
                        <div className="text-xs text-[#71717A] px-3 py-3 rounded-md bg-[#0F0F11] border border-dashed border-[#27272A] text-center">
                          Drop an audio file at the top of the form to load this track's Full Audio (HQ)
                        </div>
                      )}
                      <p className="text-[10px] text-[#71717A] mt-1">Original quality — served untouched to buyers. Also the source for the 30-sec preview trim below.</p>
                    </div>
                    <div>
                      <label className="block text-xs text-[#A1A1AA] mb-1">Preview override (optional)</label>
                      <Input
                        type="file"
                        accept="audio/*"
                        onChange={(e) => handleTrackFileChange(index, 'preview', e.target.files[0])}
                        className="bg-[#09090B] border-[#27272A] text-sm"
                        data-testid={`input-track-preview-${index}`}
                      />
                      <p className="text-[10px] text-[#71717A] mt-1">Leave blank — 30s preview is auto-generated at 192 kbps from the section you pick below</p>
                    </div>
                  </div>

                  {track.full_file && !track.preview_file && (
                    <TrackPreviewTrimmer
                      index={index}
                      track={track}
                      setTracks={setTracks}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Submit */}
          <div className="flex flex-col gap-2 pt-4">
            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-[#00F0FF] hover:bg-[#33F3FF] text-black font-medium rounded-full h-12 disabled:opacity-90 disabled:cursor-not-allowed"
                data-testid="submit-upload-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 animate-spin" size={20} />
                    <span data-testid="upload-status">{uploadStep || 'Uploading…'}</span>
                  </>
                ) : (
                  'Upload Release'
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/')}
                disabled={loading}
                className="border-[#27272A] hover:bg-[#18181B] rounded-full disabled:opacity-40"
              >
                Cancel
              </Button>
            </div>
            {loading && (
              <p className="text-[11px] text-[#71717A] text-center" data-testid="upload-hint">
                Do not close this tab — upload is in progress.
              </p>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminUploadPage;
